import type { CarouselRef } from '..';
import { SCROLL_THRESHOLD, SwipeDirection } from '../constants';

export const getSlideMeta = (carouselRef: CarouselRef) => {
  let fullyVisibleSlides = 0;
  let slideWithOffset = 0;
  let totalSlides = 0;
  if (carouselRef.current) {
    const { clientWidth, children } = carouselRef.current;
    totalSlides = children.length || 0;

    if (totalSlides > 0) {
      // Calculate actual slide width + gap from the first child element
      const firstChild = children[0] as HTMLElement;
      const computedStyle = window.getComputedStyle(carouselRef.current);
      const gap = parseInt(computedStyle.gap || '0', 10);

      slideWithOffset = firstChild.offsetWidth + gap;
      fullyVisibleSlides = Math.floor(clientWidth / slideWithOffset);
    }
  }

  return { slideWithOffset, fullyVisibleSlides, totalSlides };
};

export const getSwipeDistance = (carouselRef: CarouselRef, scrollSlidesAmount: number = 0) => {
  if (!carouselRef.current) return 0;
  const { clientWidth, scrollWidth, scrollLeft } = carouselRef.current;
  const { slideWithOffset, fullyVisibleSlides } = getSlideMeta(carouselRef);

  let scrollableSlides = scrollSlidesAmount > 0 ? scrollSlidesAmount : fullyVisibleSlides;
  if (scrollLeft + clientWidth >= scrollWidth) {
    scrollableSlides = Math.max(scrollableSlides - 1, 0);
  }
  const swipeDistance = scrollableSlides * slideWithOffset;

  return Math.ceil(swipeDistance);
};

const getChildXStart = (carouselRef: CarouselRef, reverse: boolean) => {
  if (!carouselRef.current) return [];
  const { children } = carouselRef.current;
  let start = 0;
  const parentRect = carouselRef.current.getBoundingClientRect();
  const childXStart = Array.from(children).map((child, index) => {
    const childRect = child.getBoundingClientRect();
    if (index === 0) {
      start = childRect.left - parentRect.left;
    }

    return childRect.left - parentRect.left - start;
  });

  return reverse ? childXStart.reverse() : childXStart;
};

export const getSwipePosition = (
  carouselRef: CarouselRef,
  distance: number,
  swipeDirection: SwipeDirection
) => {
  if (!carouselRef.current) return 0;
  const { scrollLeft } = carouselRef.current;
  const childStartPos = getChildXStart(carouselRef, false);

  if (childStartPos.length === 0) return scrollLeft;

  // Calculate how many cards to scroll based on distance
  const { slideWithOffset } = getSlideMeta(carouselRef);
  const currentIndex = Math.floor(scrollLeft / slideWithOffset);
  const cardsToScroll = Math.max(1, Math.round(distance / slideWithOffset));

  // Calculate target card index
  let targetIndex: number;
  if (swipeDirection === SwipeDirection.Next) {
    targetIndex = Math.min(currentIndex + cardsToScroll, childStartPos.length - 1);
  } else {
    targetIndex = Math.max(currentIndex - cardsToScroll, 0);
  }

  return childStartPos[targetIndex];
};

export const getActiveSlide = (carouselRef: CarouselRef, scrollLeft: number) => {
  const childStartPos = getChildXStart(carouselRef, true);
  const lastSlideIndex = childStartPos.length - 1;

  return lastSlideIndex - (childStartPos.findIndex(childStart => childStart <= scrollLeft) || 0);
};

export const getScrollLeftFromIndex = (carouselRef: CarouselRef, index: number) => {
  const childStartPos = getChildXStart(carouselRef, false);
  const totalSlides = childStartPos.length;

  if (totalSlides === 0 || index < 0) return 0;
  if (index >= totalSlides) return childStartPos[totalSlides - 1] || 0;

  return childStartPos[index] || 0;
};

export const isScrollable = (
  carouselRef: CarouselRef,
  scrollLeft: number,
  direction: SwipeDirection
) => {
  if (!carouselRef.current) return false;
  const { clientWidth, scrollWidth } = carouselRef.current;
  if (direction === SwipeDirection.Next) {
    return scrollLeft + clientWidth < scrollWidth - SCROLL_THRESHOLD;
  }

  return scrollLeft > SCROLL_THRESHOLD;
};

export const isSwipeable = (carouselRef: CarouselRef, direction: SwipeDirection) => {
  return isScrollable(carouselRef, carouselRef.current?.scrollLeft || 0, direction);
};
